// DEPLOY: /var/www/woody.wtf/frontend/src/chart/TimeframePicker.js
// Chart 2 timeframe picker. Desktop: the user's favourite timeframes as a segmented row (the current
// one joins it while picked) + a ▾ menu with every timeframe grouped Minutes / Hours / Days, each with a
// ☆ to add or remove it from the row. Phone: one chip with the current timeframe; tapping it opens a
// bottom sheet (favourites first, then the groups, ☆ too) - one tap picks and closes.
// EXT (pre-market / after-hours on intraday) sits next to the row while an intraday timeframe is on,
// and in the phone sheet. Minutes / hours are greyed out where there is no intraday data (non-US).
import React, { useState, useEffect, useRef } from 'react';
import { TIMEFRAMES, TF_GROUPS, tfInfo, isIntraday } from './timeframes';

const S = {
  wrap:   { position: 'relative', display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' },
  row:    { display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' },
  btn:    { padding: '5px 10px', fontSize: '12px', fontFamily: "'DM Mono',monospace", border: 'none', borderRight: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer' },
  on:     { background: 'var(--accent)', color: '#fff' },
  menu:   { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 120, width: '232px', padding: '8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)' },
  group:  { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '6px 2px 4px' },
  grid:   { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' },
  cell:   { position: 'relative', padding: '7px 0', fontSize: '12px', fontFamily: "'DM Mono',monospace", border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer', textAlign: 'center' },
  star:   { position: 'absolute', top: '-1px', right: '1px', border: 'none', background: 'none', padding: '0 2px', cursor: 'pointer', fontSize: '11px', lineHeight: 1 },
  ext:    { padding: '5px 8px', fontSize: '11px', fontWeight: 700, fontFamily: "'DM Mono',monospace", border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer' },
  chip:   { padding: '7px 12px', fontSize: '13px', fontFamily: "'DM Mono',monospace", border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer', whiteSpace: 'nowrap' },
  shade:  { position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.35)' },
  sheet:  { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 301, background: 'var(--surface)', borderRadius: '14px 14px 0 0', padding: '12px 16px calc(16px + env(safe-area-inset-bottom))', boxShadow: 'var(--shadow)' },
  sheetCell: { padding: '12px 0', fontSize: '14px' },
};

export default function TimeframePicker({ tf, favourites, onChange, onFavourites, mobile, intradayOk, ext, onExt }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open || mobile) return;
    const close = e => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open, mobile]);

  const off = t => !!t.min && !intradayOk;
  const pick = id => { setOpen(false); if (id !== tf) onChange(id); };
  const toggleFav = id => onFavourites(favourites.includes(id) ? favourites.filter(x => x !== id)
                                                                 : TIMEFRAMES.map(t => t.id).filter(x => x === id || favourites.includes(x)));
  const cell = (t, extra = {}) => (
    <button key={t.id} disabled={off(t)} onClick={() => pick(t.id)} title={off(t) ? 'Intraday candles are US only' : undefined}
            style={{ ...S.cell, ...extra, ...(tf === t.id ? S.on : {}), ...(off(t) ? { opacity: 0.35, cursor: 'default' } : {}) }}>
      {t.label}
      {(
        <span role="button" tabIndex={-1} style={{ ...S.star, ...(mobile ? { fontSize: '15px', padding: '2px 4px' } : {}), color: favourites.includes(t.id) ? '#F59E0B' : 'var(--text-muted)' }}
              title={favourites.includes(t.id) ? 'Remove from favourites' : 'Add to favourites'}
              onClick={e => { e.stopPropagation(); toggleFav(t.id); }}>{favourites.includes(t.id) ? '★' : '☆'}</span>
      )}
    </button>
  );
  const groups = extraCell => TF_GROUPS.map(g => (
    <div key={g}>
      <div style={S.group}>{g}</div>
      <div style={S.grid}>{TIMEFRAMES.filter(t => t.group === g).map(t => cell(t, extraCell))}</div>
    </div>
  ));
  const extBtn = (
    <button style={{ ...S.ext, ...(ext ? S.on : {}) }} onClick={() => onExt(!ext)}
            title={`Pre-market and after-hours candles: ${ext ? 'shown' : 'hidden'}`}>EXT</button>
  );

  if (mobile) {
    const favs = TIMEFRAMES.filter(t => favourites.includes(t.id));
    return (
      <div style={S.wrap}>
        <button style={S.chip} onClick={() => setOpen(true)}>{tfInfo(tf).label} ▾</button>
        {open && <>
          <div style={S.shade} onClick={() => setOpen(false)} />
          <div style={S.sheet}>
            {favs.length > 0 && <>
              <div style={S.group}>Favourites</div>
              <div style={S.grid}>{favs.map(t => cell(t, S.sheetCell))}</div>
            </>}
            {groups(S.sheetCell)}
            <div style={{ ...S.group, marginTop: '12px' }}>Intraday</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text)' }}>
              {extBtn}<span>Pre-market and after-hours {ext ? 'shown' : 'hidden'}</span>
            </div>
          </div>
        </>}
      </div>
    );
  }

  const row = TIMEFRAMES.filter(t => favourites.includes(t.id) || t.id === tf);
  return (
    <div ref={boxRef} style={S.wrap}>
      <div style={S.row}>
        {row.map(t => (
          <button key={t.id} disabled={off(t)} onClick={() => pick(t.id)}
                  style={{ ...S.btn, ...(tf === t.id ? S.on : {}), ...(off(t) ? { opacity: 0.35, cursor: 'default' } : {}) }}>
            {t.label}
          </button>
        ))}
        <button style={{ ...S.btn, borderRight: 'none', ...(open ? { color: 'var(--accent)' } : {}) }} title="All timeframes"
                onClick={() => setOpen(o => !o)}>▾</button>
      </div>
      {isIntraday(tf) && extBtn}
      {open && <div style={S.menu}>{groups()}</div>}
    </div>
  );
}
